import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import { periodOptions } from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import { ReportPeriod, TReportCancelFeedbackData } from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { getCancelFeedback, exportCancelFeedback } from '@src/services/reports';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportCancelFeedbackData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.reason}
        </p>
        <p className="text-white text-xs">
          Cancel Feedback: {payload[0]?.value}
        </p>
      </div>
    );
  }

  return null;
};

export const CancelFeedback: FC = () => {
  // Data state
  const [data, setData] = useState<TReportCancelFeedbackData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
  });

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportCancelFeedbackData>({
      data,
      getAccessField: (item: TReportCancelFeedbackData) => item.reason,
      getMainDataField: (item: TReportCancelFeedbackData) =>
        item.total_cancel_feedback || 0,
      includeTotalsRow: true,
      customColumns: [
        {
          title: 'CANCEL FEEDBACK',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">{item?.total_cancel_feedback ?? 0}</p>
          ),
        },
      ],
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const result = await getCancelFeedback({
        start_date: filters.startDate?.toISOString() ?? new Date(0).toISOString(),
        end_date: filters.endDate?.toISOString() ?? new Date().toISOString(),
      });
      setData(result);
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Cancel Feedback Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          dateRangeValue={filters.valueStart}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedPeriod={filters.selectedPeriod}
          onDateRangeChange={filters.setValueStart}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="reason"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                dataKey="total_cancel_feedback"
                fill="#B6D72C"
                name="Cancel Feedback"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={async () => {
                try {
                  const response = await exportCancelFeedback({
                    start_date: filters.startDate?.toISOString() ?? new Date(0).toISOString(),
                    end_date: filters.endDate?.toISOString() ?? new Date().toISOString(),
                  });
                  const url = window.URL.createObjectURL(
                    new Blob([response.data])
                  );
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'cancel_feedback.xlsx');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  toast.error('Error downloading report');
                  console.error('Error downloading report:', error);
                }
              }}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
